"use client";

import { useId, useState } from "react";
import type { PasswordSignInFailure, PasswordSubmit } from "@/lib/password-sign-in";

/**
 * ONE SENTENCE PER REASON, and the mapping is the point of the whole file.
 *
 * `~/Developer/DESIGN.md` ("Ring sign-in") states the contract these come
 * from: the verify-password endpoint answers a wrong password, an unverified
 * account and an address nobody registered with ONE response, so exactly one
 * outcome — `denied` — may be shown as a password problem. The obvious
 * implementation, `if (!ok) "wrong password"`, tells a rate-limited person to
 * retype a password that is correct, for the full fifteen minutes of a lockout
 * they may have earned in one of the seven sibling apps that share the limit.
 *
 * A `Record` over the union rather than a `switch` with a default: a seventh
 * failure mode added to `PasswordSignInFailure` is a COMPILE ERROR here, not a
 * silent fallback to whichever sentence the default happened to hold.
 *
 * Savor's voice, not giftme's: nothing urgent, nothing scolding, and no
 * exclamation. A person who cannot get in is already having a worse day than
 * the app is.
 */
const MESSAGES: Record<PasswordSignInFailure, string> = {
  denied: "That email and password don’t match.",

  // Says nothing about which of the two was wrong, and nothing about whether
  // the address has an account — the endpoint does not know either, by design.
  rate_limited: "Too many tries just now. Wait a few minutes, then try again.",

  /**
   * app_misconfigured and service_down GET THE SAME SENTENCE, deliberately.
   * They differ in whose configuration is broken — Savor's ring secret, or the
   * auth service's own — and a visitor can act on neither. What they can act
   * on is the Google button below, which is why the copy points at it.
   *
   * What this must NOT do is cast any doubt on the password. Both are Savor's
   * fault; a person told "check your password" while the fault is ours goes to
   * the reset flow and changes a password that was right all along.
   */
  app_misconfigured:
    "Password sign-in isn’t working just now. That’s on us, not on your password. Use Google below, or come back a little later.",
  service_down:
    "Password sign-in isn’t working just now. That’s on us, not on your password. Use Google below, or come back a little later.",

  // The request never produced an answer we understand: it may never have
  // arrived, or the reply may have been lost on the way back. Offers the retry
  // rather than a diagnosis, because this browser cannot tell those apart.
  unreachable: "We couldn’t reach the sign-in service. Check your connection and try again.",

  // The password was right and Savor's own admission gate still said no — a
  // revoked invite, or INVITE_ONLY. Never "that didn't match": that would send
  // somebody to reset a password that is already correct.
  not_admitted:
    "That password is right, but this account isn’t on Savor’s list. If someone invited you, open your invite link again.",
};

/**
 * The Crystal Prism password half of `/signin`, above the Google button that
 * `page.tsx` renders under it.
 *
 * `submit` is a required PROP and the route hands it the server action. A form
 * that reached for the action itself would drag `@/auth` — and NextAuth's
 * whole boot — into this module's graph, and this file's test could then not
 * run under jsdom against a suite whose `fetch` throws.
 */
export function SignInForm({ submit }: { submit: PasswordSubmit }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [pending, setPending] = useState(false);
  const [message, setMessage] = useState<string | null>(null);

  const id = useId();
  const emailId = `${id}-email`;
  const passwordId = `${id}-password`;

  async function onSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (pending) return;

    setPending(true);
    setMessage(null);

    let outcome;
    try {
      outcome = await submit(email, password);
    } catch {
      // The error VALUE is ignored on purpose: Next redacts a server action's
      // thrown message in production, so every rejection arrives as the same
      // generic string and reading it would render meaningless text. What is
      // true of every rejection is that this browser did not get an answer,
      // which is exactly what `unreachable` says.
      setMessage(MESSAGES.unreachable);
      setPending(false);
      return;
    }

    if (outcome.ok) {
      // A FULL PAGE LOAD, not a router push. The session cookie the action
      // just set is the one shared across .crystalprism.io
      // (lib/session-cookie.ts), and a hard navigation is what guarantees the
      // very next request carries it — no cached RSC payload rendered for a
      // signed-out visitor, and no dependence on a refresh landing in the
      // right order. `pending` is left ON: the page is on its way out, and
      // re-enabling the button would offer a second sign-in during the
      // navigation. That is also why @next/next's router-push rule is off for
      // this one line: `router.push` is exactly the soft navigation above.
      // eslint-disable-next-line @next/next/no-location-assign-relative-destination
      window.location.assign("/");
      return;
    }

    setMessage(MESSAGES[outcome.reason]);
    setPending(false);
  }

  return (
    <form onSubmit={onSubmit}>
      <div className="signin-field">
        <label className="signin-label" htmlFor={emailId}>Email address</label>
        <input
          id={emailId}
          className="signin-input"
          name="email"
          type="email"
          autoComplete="email"
          spellCheck={false}
          required
          // READ-ONLY, not disabled, while the attempt is in flight: a
          // `disabled` input drops keyboard focus to <body>, which is
          // precisely where the caret is for anybody who submitted with
          // Return. Savor's own rule — see InviteActions.tsx and
          // .newsrc-btn[aria-disabled] — and it costs nothing here.
          readOnly={pending}
          value={email}
          onChange={(event) => setEmail(event.target.value)}
        />
      </div>

      <div className="signin-field">
        {/* Named for WHOSE password it is. Savor has never had one of its own,
            and the address someone uses here may well be an account they made
            in a sibling app — "Password" alone invites them to invent one. */}
        <label className="signin-label" htmlFor={passwordId}>Crystal Prism password</label>
        <input
          id={passwordId}
          className="signin-input"
          name="password"
          type="password"
          autoComplete="current-password"
          required
          readOnly={pending}
          value={password}
          onChange={(event) => setPassword(event.target.value)}
        />
        {/* Two links sharing the password field's row: this app's own
            "Create an account" on the left, the ring's recovery link on the
            right — the same place and words as account.crystalprism.io/sign-in,
            so somebody who has used any other Crystal Prism surface finds
            recovery where they already looked. Plain <a>s, not next/link:
            both leave this app. DOM order matches visual order (create
            first). Both reuse `.signin-forgot` unmodified — see globals.css
            for the `.signin-links` wrapper that turns it from a full-width
            block into a shrink-to-content flex item without touching the
            padding that supplies its 44px tap row. */}
        <div className="signin-links">
          <a className="signin-forgot" href="https://auth.crystalprism.io/register?app=savor">
            Create an account
          </a>
          <a className="signin-forgot" href="https://auth.crystalprism.io/forgot?app=savor">
            Forgot your password?
          </a>
        </div>
      </div>

      {/* PRESENT IN EVERY STATE. The element carrying `role="status"` is
          rendered whether or not there is anything to say, so React reconciles
          it rather than remounting it and the live region exists before its
          content does — a region created at the same moment as its message
          announces nothing at all. It is a real, visible box in the
          accessibility tree at all times: not `display: none`, not
          `visibility: hidden`, not `display: contents`, each of which takes it
          back out of that tree and reintroduces the defect in a form that
          looks fixed. Empty, it must also cost no space, which is why this
          form is laid out with MARGINS and not `gap` — see globals.css.

          `role="status"` rather than `role="alert"`: status is polite and
          atomic, so the sentence is read once, after whatever the reader was
          already saying. An alert interrupts, and the message is never urgent
          — the person is looking straight at the form they just submitted. */}
      <div className="signin-live" role="status">
        {message ? <p className="signin-fault">{message}</p> : null}
      </div>

      {/* `aria-disabled`, not `disabled`, with an inert handler — Savor's rule
          for every busy control (InviteActions.tsx): `disabled` drops keyboard
          focus to <body> mid-submit, and this button is where focus lives
          after a click. The `pending` guard in onSubmit is what actually
          prevents a second attempt. */}
      <button
        type="submit"
        className="signin-submit hover-deepen"
        aria-disabled={pending}
        aria-busy={pending}
        onClick={(event) => { if (pending) event.preventDefault(); }}
      >
        {pending ? "Signing in…" : "Sign in"}
      </button>
    </form>
  );
}
